import { useProjectParams } from '@hooks/useProjectParams';
import { ChooseSectionText } from 'components/ChooseSectionText';
import { DropdownContext } from 'components/DropdownContext';
import { SideDetailsLayout } from 'components/Layout/SideDetailsLayout';
import {
    findAllConstructionTypesFromObjects,
    WorkOrderConstructionTypesFromObjects,
} from 'components/WorkOrderConstructionTypesTabSelect';
import { useRequest } from 'features/request/useRequest';
import type { WorkOrderChangeSeasonData } from 'gql/graphql';
import {
    ContractStatusEnum,
    ProjectStatusEnum,
    SectionEnum,
    WorkOrderChangeStatusEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { useProjectData } from 'hooks/useProjectData';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { WorkOrderFilterForm } from 'pages/Project/UerProject/WorkOrder/WorkOrderFilterForm';
import { WorkOrderChapters } from 'pages/Project/UerProject/WorkOrder/workorderTypes';
import { Button, cnMixSpace, Grid, GridItem, IconArrowDown, Layout, Tooltip } from 'uikit';
import { destroyAgGridTableFilters } from 'utils';
import { workOrderChangeDescriptionNumberAndDate } from 'utils/descriptionNumberAndDate';
import { getLabelWorkOrderNoSingingDate } from 'utils/getLabelWorkOrder';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useContractData } from '../../Contract/hooks/useContractData';
import { WorkOrderUseCoefficient } from '../../WorkOrderUseCoefficientModal/WorkOrderUseCoefficient';
import { AddOtherToWorkOrderChange } from '../AddOtherToWorkOrderChange';
import { AddUerToWorkOrderChange } from '../AddUerToWorkOrderChange';
import { useGetWorkOrderChange, useWorkOrderChangeObjects } from '../hooks';
import { MoveVolumesBetweenSeasonsForObject } from '../MoveVolumesBetweenSeasonsForObject';
import { WorkOrderChangeDraftInformer } from '../WorkOrderChangeDraftInformer';
import { WorkOrderChangeOtherPrice } from '../WorkOrderChangeOtherPrice';
import { WorkOrderChangePrice } from '../WorkOrderChangePrice';
import { getWorkOrderSeasons } from '../WorkOrderChangeQueries';
import { WorkOrderChangeSummary } from '../WorkOrderChangeSummary/WorkOrderChangeSummary';
import { WorkOrderChangeSummaryTable } from '../WorkOrderChangeSummaryTable';
import { WorkOrderChangeToolbar } from '../WorkOrderChangeToolbar';

import { useAddToWorkOrderChangeMenu } from './hooks/useAddToWorkOrderChangeMenu';
import { useCorrectUpdateWorkOrderChangeUerData } from './hooks/useCorrectUpdateWorkOrderChangeUerData';

import '../workOrderChange.css';

export type TFilters = {
    chapter?: WorkOrderChapters | undefined;
    objectId?: number | undefined;
    subObjectId?: number | undefined;
};
const WORK_ORDER_CHANGE_PATH_PATTERN = '/projects/:methodology/:id/orderChange/:orderId/old';

export const WorkOrderChange = () => {
    const dispatch = useDispatch();
    const filterRef = useRef<HTMLDivElement>(null);
    const currentPriceTableGridApiRef = useRef<(AgGridReact<any> & HTMLDivElement) | null>(null);
    const currentPriceTableWithClientModelUpdaterRef = useRef<() => void>();
    const refreshDraftInfoRef = useRef<{ refreshDraftInfo: () => void }>();
    const [position, setPosition] = useState<number | undefined>();

    const { isHeaderOpened } = useContext(PageHeaderLayoutContext);

    const refreshCurrentTableData = useCallback(() => {
        if (currentPriceTableGridApiRef?.current) {
            currentPriceTableGridApiRef.current?.api?.refreshInfiniteCache();
        }

        if (refreshDraftInfoRef?.current?.refreshDraftInfo) {
            refreshDraftInfoRef?.current?.refreshDraftInfo();
        }
    }, [currentPriceTableGridApiRef]);

    const refreshCurrentTableDataWithClientModelCreator = useCallback((refreshFn: () => void) => {
        currentPriceTableWithClientModelUpdaterRef.current = refreshFn;
    }, []);

    const refreshCurrentTableDataWithClientModel = useCallback(
        () => {
            currentPriceTableWithClientModelUpdaterRef?.current?.();
        },
        // eslint-disable-next-line
        [currentPriceTableWithClientModelUpdaterRef?.current],
    );

    const { id: projectId, orderChangeId, methodology } = useProjectParams();

    const { projectData, refreshProject } = useProjectData(projectId);

    const { workOrderChangeUer: workOrderChangeUerRules } = useAccess();

    const contractId = useMemo(() => projectData?.contractId, [projectData?.contractId]);
    const statusProject = projectData?.status;
    const statusContract = projectData?.contractNavigation?.data?.contractNavData?.status;

    const [contractData] = useContractData(contractId);

    const catalogUerBaseName = contractData?.catalogUerBase?.name || '';
    const catalogVersion = contractData?.catalogUerBase?.version;

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = statusProject === ProjectStatusEnum.Completed;

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;

    const [constructionTypeId, setConstructionTypeId] = useState(0);
    const [filters, setFilters] = useState<TFilters>({});
    const workOrderChangeId = useMemo(() => Number(orderChangeId), [orderChangeId]);

    const seasonsVariables = useMemo(() => ({ workOrderChangeId }), [workOrderChangeId]);

    const [workOrderChangeSeasonsData, , , getWorkOrderSeasonsData] = useRequest({
        requestId: 'workOrderChangeSeasons',
        query: getWorkOrderSeasons,
        variables: seasonsVariables,
    });

    const refreshSeasonsDataHandler = useCallback(() => {
        dispatch(getWorkOrderSeasonsData(seasonsVariables));
    }, [dispatch, getWorkOrderSeasonsData, seasonsVariables]);

    const workOrderChangeSeasons: WorkOrderChangeSeasonData[] = useMemo(
        () => workOrderChangeSeasonsData?.uerWorkOrderChangeSeasons?.items || [],
        [workOrderChangeSeasonsData?.uerWorkOrderChangeSeasons?.items],
    );

    const { workOrderChangeData, refreshWorkOrderChangeData } = useGetWorkOrderChange({
        workOrderChangeId,
    });

    const { workOrderChangeObjectsDataPatched, workOrderChangeObjectsData, refreshWorkOrderChangeObjects } =
        useWorkOrderChangeObjects({
            workOrderChangeId,
        });

    const workOrderChangeStatusOnFormation = workOrderChangeData?.status === WorkOrderChangeStatusEnum.OnFormation;

    const checkedWorkOrderNavs = useMemo(
        () =>
            projectData?.contractNavigation?.data?.workOrderNavs?.find(
                (workOrder) => workOrder.id === workOrderChangeData?.workOrderId,
            ),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderChangeData?.workOrderId],
    );

    const isWorkOrderDelayed = checkedWorkOrderNavs?.status === WorkOrderStatusEnum.Delayed;

    const titleWorkOrderChange = useMemo(
        () =>
            `Изм ${workOrderChangeDescriptionNumberAndDate(
                workOrderChangeData?.internalNumber,
                workOrderChangeData?.signingDate,
                workOrderChangeData?.workOrderNumberText,
            )}`,
        [
            workOrderChangeData?.internalNumber,
            workOrderChangeData?.signingDate,
            workOrderChangeData?.workOrderNumberText,
        ],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
            {
                label: getLabelWorkOrderNoSingingDate(checkedWorkOrderNavs),
                href: `/projects/${methodology}/${projectData?.uniqueId}/order/${checkedWorkOrderNavs?.id}`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId, checkedWorkOrderNavs],
    );

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: ({ savedValuesObj }) => {
                    const savedConstructionTypeId = savedValuesObj?.constructionTypeId;
                    const savedFilters = savedValuesObj?.[String(savedConstructionTypeId)];

                    if (savedConstructionTypeId) {
                        setConstructionTypeId(savedConstructionTypeId);
                    }

                    setFilters(savedFilters ?? {});
                },
                withEmptyFilterSetter: (setter: (params: { key: string | undefined; newState: any }) => void) => {
                    const newConstructionTypeId = findAllConstructionTypesFromObjects({
                        workOrderChangeObjectsData,
                    })?.[0]?.id;

                    if (newConstructionTypeId) {
                        setConstructionTypeId(newConstructionTypeId);
                        setter({
                            key: 'constructionTypeId',
                            newState: newConstructionTypeId,
                        });
                    }
                },
                matchPathPattern: WORK_ORDER_CHANGE_PATH_PATTERN,
                unableToProcess: !workOrderChangeObjectsDataPatched?.length,
            }),
            [workOrderChangeObjectsData, workOrderChangeObjectsDataPatched?.length],
        ),
    );

    const changeConstructionTypeHandler = useCallback(
        (newConstructionType) => {
            setConstructionTypeId(newConstructionType.id);

            setFilters(savedFilterState?.[newConstructionType.id] ?? {});

            handleSaveFilterState({
                key: `constructionTypeId`,
                newState: newConstructionType?.id,
            });
        },
        [handleSaveFilterState, savedFilterState],
    );

    const filterChangeHandler = useCallback(
        (newFilters: TFilters) => {
            let mergedFilters: TFilters = {};

            setFilters((oldFilters: TFilters) => {
                if (newFilters.chapter === WorkOrderChapters.SUMMARY) {
                    mergedFilters = { ...newFilters, objectId: undefined, subObjectId: undefined };

                    return mergedFilters;
                }

                if (
                    oldFilters.chapter &&
                    oldFilters.chapter !== newFilters.chapter &&
                    newFilters.chapter === WorkOrderChapters.OTHERS
                ) {
                    mergedFilters = { ...oldFilters, ...newFilters, subObjectId: undefined };

                    return mergedFilters;
                }

                mergedFilters = { ...oldFilters, ...newFilters };

                return mergedFilters;
            });

            handleSaveFilterState({
                key: String(constructionTypeId),
                newState: mergedFilters,
            });
        },
        [constructionTypeId, handleSaveFilterState],
    );

    const isPriceChaptersSelected = useMemo(
        () =>
            filters.chapter
                ? [WorkOrderChapters.CHAPTER1_7, WorkOrderChapters.CHAPTER8, WorkOrderChapters.CHAPTER9].includes(
                      filters.chapter,
                  )
                : false,
        [filters.chapter],
    );

    const isOthersChapterSelected = useMemo(() => filters.chapter === WorkOrderChapters.OTHERS, [filters.chapter]);

    const isSummaryChapterSelected = useMemo(() => filters.chapter === WorkOrderChapters.SUMMARY, [filters.chapter]);

    const isShowTablePossible = useMemo(
        () =>
            (isSummaryChapterSelected && filters.chapter) ||
            (isPriceChaptersSelected && filters.subObjectId && filters.chapter) ||
            (isOthersChapterSelected && filters.objectId && filters.chapter),
        [
            filters.chapter,
            filters.objectId,
            filters.subObjectId,
            isOthersChapterSelected,
            isPriceChaptersSelected,
            isSummaryChapterSelected,
        ],
    );

    const [isSummaryDetailsVisible, setIsSummaryDetailsVisible] = useState(false);

    const onSummaryDetailsVisibilityChange = useCallback((isVisible) => {
        setIsSummaryDetailsVisible(isVisible);
    }, []);

    const actionsDisabled = useMemo(
        () =>
            !workOrderChangeStatusOnFormation ||
            !isContractStatusWork ||
            isProjectStatusDelayed ||
            isProjectStatusCompleted ||
            isWorkOrderDelayed,
        [
            isContractStatusWork,
            isWorkOrderDelayed,
            isProjectStatusCompleted,
            isProjectStatusDelayed,
            workOrderChangeStatusOnFormation,
        ],
    );

    const isAddOthersDisabled = useMemo(
        () => !isOthersChapterSelected || !isShowTablePossible,
        [isOthersChapterSelected, isShowTablePossible],
    );

    const isMoveVolumesDisabled = useMemo(
        () =>
            !filters.objectId ||
            !workOrderChangeStatusOnFormation ||
            workOrderChangeSeasons.length <= 1 ||
            (workOrderChangeStatusOnFormation && isWorkOrderDelayed),
        [filters.objectId, isWorkOrderDelayed, workOrderChangeSeasons.length, workOrderChangeStatusOnFormation],
    );

    const costRounding = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding],
    );

    const [isMoveVolumesOpened, setIsMoveVolumesOpened] = useState(false);

    const onMoveVolumesClick = useCallback(() => setIsMoveVolumesOpened(true), []);
    const onMoveVolumesClose = useCallback(() => setIsMoveVolumesOpened(false), []);

    useEffect(() => {
        if (filterRef.current) {
            setPosition(filterRef.current?.getBoundingClientRect?.().bottom);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterRef, isHeaderOpened, filterRef.current?.getBoundingClientRect?.().bottom]);

    const {
        isOpenedModalPrices,
        isOpenedModalOthers,
        isOpenedModalCoefficient,
        closeModalsHandler,
        menuItems,
        getMenuItemAttributes,
        onMenuMouseMove,
        onMenuMouseLeave,
        activeTooltipMsg,
        tooltipPosition,
    } = useAddToWorkOrderChangeMenu({
        filters,
        actionsDisabled,
        workOrderChangeStatus: workOrderChangeData?.status,
    });

    const { workOrderObjectId, workOrderChangeObjectId, workOrderRootObjectId, workOrderChangeRootObjectId } = useMemo(
        () =>
            workOrderChangeObjectsDataPatched?.find(
                (w) => w.id === (isOthersChapterSelected ? filters.objectId : filters.subObjectId),
            ) || ({} as any),
        [filters.objectId, filters.subObjectId, isOthersChapterSelected, workOrderChangeObjectsDataPatched],
    );

    const { makeCorrectRefreshTableData } = useCorrectUpdateWorkOrderChangeUerData({
        workOrderObjectId,
        workOrderChangeObjectId,
        objectsData: workOrderChangeObjectsDataPatched,
        refreshWorkOrderChangeObjects,
        refreshTableFn: useMemo(
            () => [refreshCurrentTableDataWithClientModel, refreshCurrentTableData],
            [refreshCurrentTableDataWithClientModel, refreshCurrentTableData],
        ),
    });

    const refreshOtherWithFilterReset = useCallback(() => {
        makeCorrectRefreshTableData([]);
        destroyAgGridTableFilters({
            api: currentPriceTableGridApiRef?.current?.api,
            columns: ['name', 'status'],
        });
    }, [makeCorrectRefreshTableData]);

    return (
        <ProjectPageLayout
            title={titleWorkOrderChange}
            statusWorkOrderChange={workOrderChangeData?.status}
            breadCrumbsItems={breadCrumbsItems}
            style={{ marginBottom: 0 }}
            headerRightSide={
                <WorkOrderChangeDraftInformer
                    workOrderChangeId={workOrderChangeId}
                    status={workOrderChangeData?.status}
                    refreshDraftInfoRef={refreshDraftInfoRef}
                />
            }
            isHeaderCollapsable
            layoutEntityKey={orderChangeId}
            headerBottom={
                <WorkOrderChangeToolbar
                    refreshProject={refreshProject}
                    workOrderChangeData={workOrderChangeData}
                    projectData={projectData}
                    refreshWorkOrderChangeData={refreshWorkOrderChangeData}
                    isActionsDisabled={actionsDisabled}
                    statusContract={statusContract}
                    statusProject={statusProject}
                    workOrderChangeUerRules={workOrderChangeUerRules}
                    refreshTable={refreshCurrentTableData}
                    refreshWorkOrderChangeObjects={refreshWorkOrderChangeObjects}
                    refreshSeasonsData={refreshSeasonsDataHandler}
                />
            }
        >
            <WorkOrderConstructionTypesFromObjects
                workOrderChangeObjectsData={workOrderChangeObjectsData}
                onChange={changeConstructionTypeHandler}
                activeConstructionTypeId={constructionTypeId}
            />
            <Grid
                cols={12}
                gap="xl"
                className={`work-order-change-filter ${cnMixSpace({ mT: 'xl', mB: 'xl' })}`}
                style={{ overflow: 'auto' }}
                ref={filterRef}
            >
                <GridItem col={7}>
                    <WorkOrderFilterForm
                        defaultValues={filters}
                        onChange={filterChangeHandler}
                        constructionTypeId={constructionTypeId}
                        workOrderObjects={workOrderChangeObjectsDataPatched}
                    />
                </GridItem>
                <GridItem col={3}>
                    <Layout>
                        {filters.chapter &&
                            [
                                WorkOrderChapters.CHAPTER1_7,
                                WorkOrderChapters.CHAPTER8,
                                WorkOrderChapters.CHAPTER9,
                            ].includes(filters.chapter) && (
                                <Button
                                    onClick={onMoveVolumesClick}
                                    label="Перенести объемы"
                                    size="s"
                                    view="secondary"
                                    className="moveVolumesButton"
                                    disabled={isMoveVolumesDisabled}
                                />
                            )}
                        <DropdownContext
                            wrapperClassName={cnMixSpace({ mR: 'xs' })}
                            items={menuItems}
                            btnView="primary"
                            btnSize="s"
                            btnIconSize="s"
                            btnLabel="Добавить"
                            icon={IconArrowDown}
                            getItemAttributesHandler={getMenuItemAttributes}
                            onMouseMoveHandler={onMenuMouseMove}
                            onMouseLeaveHandler={onMenuMouseLeave}
                        />
                        {activeTooltipMsg && (
                            <Tooltip
                                size="s"
                                direction="upCenter"
                                position={tooltipPosition}
                                isInteractive={false}
                                style={{ zIndex: 2 }}
                            >
                                {activeTooltipMsg}
                            </Tooltip>
                        )}
                        <AddUerToWorkOrderChange
                            isModalOpenedOuter={isOpenedModalPrices}
                            closeModalOuter={closeModalsHandler}
                            workOrderChangeObjectId={workOrderChangeObjectId}
                            chapter={filters.chapter}
                            constructionTypeId={constructionTypeId}
                            refreshTable={() => {
                                makeCorrectRefreshTableData([]);
                            }}
                            contractId={contractId}
                            isActionsDisabled={actionsDisabled}
                            workOrderChangeStatus={workOrderChangeData?.status}
                            workOrderChangeUerRules={workOrderChangeUerRules}
                            filters={filters}
                            workOrderChangeId={workOrderChangeId}
                            workOrderObjectId={workOrderObjectId}
                            catalogUerBaseName={catalogUerBaseName}
                            catalogVersion={catalogVersion}
                        />
                        <AddOtherToWorkOrderChange
                            isModalOpenedOuter={isOpenedModalOthers}
                            closeModalOuter={closeModalsHandler}
                            isDisabled={isAddOthersDisabled || actionsDisabled}
                            constructionTypeId={constructionTypeId}
                            workOrderChangeObjectsData={workOrderChangeObjectsDataPatched}
                            refreshOthersTable={refreshOtherWithFilterReset}
                            filters={filters}
                            workOrderChangeStatus={workOrderChangeData?.status}
                            workOrderChangeUerRules={workOrderChangeUerRules}
                        />
                        <MoveVolumesBetweenSeasonsForObject
                            isOpen={isMoveVolumesOpened}
                            onClose={onMoveVolumesClose}
                            refreshTableDataHandler={() => {
                                makeCorrectRefreshTableData([]);
                            }}
                            workOrderChangeSeasons={workOrderChangeSeasons}
                            workOrderChangeId={workOrderChangeId}
                            workOrderRootObjectId={
                                filters.subObjectId === filters.objectId ? workOrderObjectId : workOrderRootObjectId
                            }
                            workOrderChangeRootObjectId={
                                filters.subObjectId === filters.objectId
                                    ? workOrderChangeObjectId
                                    : workOrderChangeRootObjectId
                            }
                        />
                    </Layout>
                </GridItem>
            </Grid>
            {isOpenedModalCoefficient && (
                <WorkOrderUseCoefficient
                    workOrderObjects={workOrderChangeObjectsDataPatched}
                    constructionTypeId={constructionTypeId}
                    closeHandler={closeModalsHandler}
                    workOrderChangeId={workOrderChangeId}
                    updaterApplyRatio={refreshCurrentTableData}
                    objectId={filters?.objectId}
                />
            )}
            <div
                id="workOrderChange"
                style={{
                    height: `calc(100vh - ${(position ?? 0) + 74}px)`,
                }}
            >
                {isShowTablePossible ? (
                    <>
                        {(isPriceChaptersSelected || isOthersChapterSelected) && (
                            <SideDetailsLayout
                                onDetailsVisibilityChange={onSummaryDetailsVisibilityChange}
                                collapsedTitle="Показать итоги"
                                expandedTitle="Скрыть итоги"
                                switchStyle={{ marginTop: 30, height: 'calc(100% + 2px)' }}
                            >
                                {isPriceChaptersSelected && (
                                    <WorkOrderChangePrice
                                        chapter={filters.chapter as any}
                                        workOrderChangeId={workOrderChangeId}
                                        workOrderObjectId={workOrderObjectId}
                                        workOrderChangeObjectId={workOrderChangeObjectId}
                                        ref={currentPriceTableGridApiRef}
                                        refreshTable={() => {
                                            makeCorrectRefreshTableData([]);
                                        }}
                                        isActionsDisabled={actionsDisabled}
                                        constructionTypeId={constructionTypeId}
                                        statusWorkOrderChange={workOrderChangeData?.status}
                                        workOrderChangeUerRules={workOrderChangeUerRules}
                                        costRounding={costRounding}
                                        workOrderChangeSeasons={workOrderChangeSeasons}
                                        contractId={contractId}
                                        refreshProject={refreshProject}
                                    />
                                )}
                                {isOthersChapterSelected && (
                                    <WorkOrderChangeOtherPrice
                                        workOrderChangeId={workOrderChangeId}
                                        workOrderObjectId={workOrderObjectId}
                                        workOrderChangeObjectId={workOrderChangeObjectId}
                                        createUpdateFn={refreshCurrentTableDataWithClientModelCreator}
                                        workOrderChangeObjectsData={workOrderChangeObjectsDataPatched}
                                        constructionTypeId={constructionTypeId}
                                        isActionsDisabled={actionsDisabled}
                                        contractId={contractId}
                                        ref={currentPriceTableGridApiRef}
                                        workOrderChangeUerRules={workOrderChangeUerRules}
                                        costRounding={costRounding}
                                        workOrderChangeSeasons={workOrderChangeSeasons}
                                    />
                                )}
                                {isSummaryDetailsVisible && (
                                    <WorkOrderChangeSummary
                                        isVisible={isSummaryDetailsVisible}
                                        workOrderChangeObjectId={workOrderChangeObjectId}
                                        chapter={(isPriceChaptersSelected ? filters.chapter : null) as any}
                                        section={isOthersChapterSelected ? SectionEnum.CustomerLimits : undefined}
                                        costRounding={costRounding}
                                        workOrderChangeId={workOrderChangeId}
                                        workOrderObjectId={workOrderObjectId}
                                    />
                                )}
                            </SideDetailsLayout>
                        )}
                        {isSummaryChapterSelected && (
                            <WorkOrderChangeSummaryTable
                                workOrderChangeId={workOrderChangeId}
                                costRounding={costRounding}
                                workOrderChangeSeasons={workOrderChangeSeasons}
                            />
                        )}
                    </>
                ) : (
                    <ChooseSectionText text="Для отображения данных выберите раздел, объект и подобъект" />
                )}
            </div>
        </ProjectPageLayout>
    );
};

WorkOrderChange.displayName = 'WorkOrderChange';
